import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { Button } from "./button";
import { Alert, AlertDescription, AlertTitle } from "./alert";
import { cn } from "@quake/shared-ui/lib/utils";
import AlertTriangleIcon from "lucide-react/dist/esm/icons/alert-triangle";
import RefreshCwIcon from "lucide-react/dist/esm/icons/refresh-cw";

const errorBoundaryVariants = cva("", {
  variants: {
    variant: {
      default: "container mx-auto py-8",
      minimal: "p-4",
      inline: "w-full",
      fullscreen: "min-h-screen flex items-center justify-center",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface ErrorInfo {
  componentStack: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

interface ErrorBoundaryProps extends VariantProps<typeof errorBoundaryVariants> {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  maxRetries?: number;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number | object>;
  className?: string;
}

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  resetError: () => void;
  retryCount: number;
  maxRetries: number;
  variant?: "default" | "minimal" | "inline" | "fullscreen" | null;
}

export function DefaultErrorFallback({
  error,
  resetError,
  retryCount,
  maxRetries,
  variant = "default",
}: ErrorFallbackProps) {
  const isMinimal = variant === "minimal" || variant === "inline";

  if (isMinimal) {
    return (
      <Alert variant="destructive" className="w-full">
        <AlertTriangleIcon className="h-4 w-4" />
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription className="mt-2 flex items-center justify-between">
          <span>An error occurred while rendering this component.</span>
          {retryCount < maxRetries && (
            <Button variant="outline" size="sm" onClick={resetError} className="ml-2">
              <RefreshCwIcon className="h-3 w-3 mr-1" />
              Retry
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center text-center space-y-6">
      <div className="rounded-full bg-red-100 p-4">
        <AlertTriangleIcon className="h-12 w-12 text-red-600" />
      </div>

      <div className="space-y-3 max-w-md">
        <h2 className="text-2xl font-bold tracking-tight">Something went wrong</h2>
        <p className="text-muted-foreground">
          We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
        </p>

        {process.env.NODE_ENV === "development" && error && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
              Error Details (Development)
            </summary>
            <pre className="mt-2 text-xs bg-muted p-3 rounded overflow-auto max-h-40">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
      </div>

      <div className="flex gap-3">
        {retryCount < maxRetries && (
          <Button onClick={resetError} variant="default">
            <RefreshCwIcon className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        )}
        <Button variant="outline" onClick={() => window.location.reload()}>
          Refresh Page
        </Button>
      </div>

      {maxRetries
        ? retryCount >= maxRetries && (
            <p className="text-sm text-muted-foreground">Maximum retry attempts reached. Please refresh the page.</p>
          )
        : null}
    </div>
  );
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      errorInfo,
    });

    // Call the onError callback if provided
    this.props.onError?.(error, errorInfo);

    // Log error in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset error state if resetKeys have changed
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some((key, index) => key !== prevProps.resetKeys?.[index]);
      if (hasResetKeyChanged) {
        this.resetError();
      }
    }

    // Reset error state if any props have changed and resetOnPropsChange is true
    if (hasError && resetOnPropsChange && prevProps !== this.props) {
      this.resetError();
    }
  }

  resetError = () => {
    const { maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    if (retryCount < maxRetries) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: retryCount + 1,
      });
    }
  };

  render() {
    const { hasError, error, errorInfo, retryCount } = this.state;
    const {
      children,
      fallback: FallbackComponent = DefaultErrorFallback,
      variant,
      className,
      maxRetries = 3,
    } = this.props;

    if (hasError) {
      return (
        <div className={cn(errorBoundaryVariants({ variant }), className)}>
          <FallbackComponent
            error={error}
            errorInfo={errorInfo}
            resetError={this.resetError}
            retryCount={retryCount}
            maxRetries={maxRetries}
            variant={variant}
          />
        </div>
      );
    }

    return children;
  }
}

export function RouteErrorComponent({ error }: ErrorComponentProps) {
  return (
    <div className={cn(errorBoundaryVariants({ variant: "default" }))}>
      <DefaultErrorFallback
        error={error as Error}
        errorInfo={null}
        resetError={() => window.location.reload()}
        retryCount={0}
        maxRetries={0}
        variant="default"
      />
    </div>
  );
}

// Hook version for functional components
export function useErrorBoundary() {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    setError(error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { captureError, resetError };
}

// Higher-order component wrapper
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, "children">,
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

export type { ErrorBoundaryProps, ErrorFallbackProps };
